import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createOverwatchWidgetRequests,
  isTerminalOverwatchStatus,
  OVERWATCH_POLL_GIVE_UP_MS,
  OVERWATCH_POLL_INTERVAL_MS,
  OVERWATCH_REFRESH_WINDOW_MS,
  OverwatchFailure,
  OverwatchOutcome,
  pollOverwatchWidgetRequests
} from 'api/overwatch';
import { useTypedSelector } from 'hooks';

import { IOverwatchEnvelope, IOverwatchRequestError, IOverwatchWidgetRequest } from './types';

export type OverwatchWidgetStatus = 'loading' | 'completed' | 'failed';

/** Failures the frontend raises itself, never sent by the API. */
export interface IOverwatchClientError {
  code: 'poll_give_up' | 'request_failed';
  failure?: OverwatchFailure;
}

export interface IOverwatchWidgetState {
  status: OverwatchWidgetStatus;
  requestId: number | null;
  /** One envelope per data key; kept from the last completion while a refresh is loading. */
  envelopes: Record<string, IOverwatchEnvelope> | null;
  error: IOverwatchRequestError | IOverwatchClientError | null;
}

export interface UseOverwatchWidgetDataOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
  giveUpMs?: number;
}

export interface UseOverwatchWidgetDataResult {
  widgets: Record<string, IOverwatchWidgetState>;
  isPolling: boolean;
  /** Resolves `rateLimited` without a request while the 15-minute window is known to be open. */
  refresh: () => Promise<OverwatchOutcome<null>>;
  refreshAvailableAt: number | null;
}

type WidgetMap = Record<string, IOverwatchWidgetState>;

const failAll = (map: WidgetMap, keys: string[], error: IOverwatchClientError): WidgetMap => {
  const next = { ...map };

  keys.forEach(key => {
    next[key] = {
      requestId: next[key]?.requestId ?? null,
      envelopes: next[key]?.envelopes ?? null,
      status: 'failed',
      error
    };
  });

  return next;
};

const loadingMap = (keys: string[]): WidgetMap =>
  Object.fromEntries(
    keys.map(key => [key, { status: 'loading', requestId: null, envelopes: null, error: null }])
  );

const useOverwatchWidgetData = (
  keys: string[],
  {
    enabled = true,
    pollIntervalMs = OVERWATCH_POLL_INTERVAL_MS,
    giveUpMs = OVERWATCH_POLL_GIVE_UP_MS
  }: UseOverwatchWidgetDataOptions = {}
): UseOverwatchWidgetDataResult => {
  const tenantId = useTypedSelector(state => state.global?.tenantConfig?.id);
  const keySignature = Array.from(new Set(keys)).join('\n');
  const uniqueKeys = useMemo(() => (keySignature ? keySignature.split('\n') : []), [keySignature]);

  const [widgets, setWidgets] = useState<WidgetMap>({});
  const [isPolling, setIsPolling] = useState(false);
  const [refreshAvailableAt, setRefreshAvailableAt] = useState<number | null>(null);

  // Bumped on every new batch, tenant switch and unmount; a stale async step compares and stops.
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keyById = useRef<Map<number, string>>(new Map());

  const stop = useCallback(() => {
    generation.current += 1;

    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const applyEntries = useCallback((entries: IOverwatchWidgetRequest[]) => {
    setWidgets(current => {
      const next = { ...current };

      entries.forEach(entry => {
        const key = keyById.current.get(entry.id);

        if (!key) {
          return;
        }

        next[key] = {
          requestId: entry.id,
          status: isTerminalOverwatchStatus(entry.status)
            ? (entry.status as OverwatchWidgetStatus)
            : 'loading',
          envelopes: entry.status === 'completed' ? entry.data : next[key]?.envelopes ?? null,
          error: entry.error
        };
      });

      return next;
    });
  }, []);

  const run = useCallback(
    async (refresh: boolean): Promise<OverwatchOutcome<null>> => {
      if (uniqueKeys.length === 0) {
        return { kind: 'ok', data: null };
      }

      const requestGeneration = generation.current;
      const outcome = await createOverwatchWidgetRequests({ keys: uniqueKeys, refresh });

      if (generation.current !== requestGeneration) {
        return outcome.kind === 'ok' ? { kind: 'ok', data: null } : outcome;
      }

      if (outcome.kind !== 'ok') {
        if (outcome.kind === 'rateLimited') {
          setRefreshAvailableAt(Date.now() + outcome.retryAfterMs);
        } else if (!refresh) {
          setWidgets(current =>
            failAll(current, uniqueKeys, { code: 'request_failed', failure: outcome })
          );
        }

        return outcome;
      }

      stop();
      const runGeneration = generation.current;
      const pending = new Set<number>();

      keyById.current = new Map();
      outcome.data.forEach((entry, index) => {
        keyById.current.set(entry.id, entry.key ?? uniqueKeys[index]);

        if (!isTerminalOverwatchStatus(entry.status)) {
          pending.add(entry.id);
        }
      });
      applyEntries(outcome.data);

      if (refresh) {
        setRefreshAvailableAt(Date.now() + OVERWATCH_REFRESH_WINDOW_MS);
      }

      const startedAt = Date.now();
      const keysOf = (ids: Set<number>) => Array.from(ids, id => keyById.current.get(id) as string);

      const tick = () => {
        if (generation.current !== runGeneration) {
          return;
        }

        if (pending.size === 0) {
          setIsPolling(false);

          return;
        }

        if (Date.now() - startedAt >= giveUpMs) {
          const stuck = keysOf(pending);

          setWidgets(current => failAll(current, stuck, { code: 'poll_give_up' }));
          setIsPolling(false);

          return;
        }

        timer.current = setTimeout(async () => {
          timer.current = null;
          const polled = await pollOverwatchWidgetRequests(Array.from(pending));

          if (generation.current !== runGeneration) {
            return;
          }

          if (polled.kind === 'ok') {
            polled.data.forEach(entry => {
              if (isTerminalOverwatchStatus(entry.status)) {
                pending.delete(entry.id);
              }
            });
            applyEntries(polled.data);
            // A network blip or a 5xx is retried on the next tick until the give-up time.
          } else if (polled.kind !== 'network' && polled.kind !== 'serverError') {
            const failed = keysOf(pending);

            pending.clear();
            setWidgets(current =>
              failAll(current, failed, { code: 'request_failed', failure: polled })
            );
          }

          tick();
        }, pollIntervalMs);
      };

      setIsPolling(pending.size > 0);
      tick();

      return { kind: 'ok', data: null };
    },
    [applyEntries, giveUpMs, pollIntervalMs, stop, uniqueKeys]
  );

  useEffect(() => {
    const active = enabled && Boolean(tenantId) && uniqueKeys.length > 0;

    setWidgets(active ? loadingMap(uniqueKeys) : {});
    setIsPolling(false);

    if (!active) {
      return undefined;
    }

    run(false);

    return stop;
  }, [enabled, tenantId, uniqueKeys, run, stop]);

  // The refresh limit is per user and tenant, so a new tenant starts with a fresh window.
  useEffect(() => {
    setRefreshAvailableAt(null);
  }, [tenantId]);

  const refresh = useCallback(async (): Promise<OverwatchOutcome<null>> => {
    const now = Date.now();

    if (refreshAvailableAt !== null && refreshAvailableAt > now) {
      return { kind: 'rateLimited', message: null, retryAfterMs: refreshAvailableAt - now };
    }

    return run(true);
  }, [refreshAvailableAt, run]);

  return { widgets, isPolling, refresh, refreshAvailableAt };
};

export default useOverwatchWidgetData;
